import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminWebshops } from '@/api/(admin)/webshops';

export interface WebshopsQueryInput {
  page: number;
  limit: number;
  sort?: string | undefined;
  filters: Record<string, string[]>;
}

/** Every filter the list endpoint accepts; anything else is dropped. */
const FILTER_KEYS = new Set([
  'shopId',
  'companyId',
  'name',
  'shopPlatform',
  'isBlocked',
]);

/**
 * The table's filter state as the endpoint's `filter` JSON.
 *
 * `useTable` holds each filter as an array because the pills are multi-select,
 * but this endpoint matches one value per column, so the first is the one sent.
 */
function buildFilterJson(filters: Record<string, string[]>): string | null {
  const out: Record<string, string> = {};
  for (const [key, values] of Object.entries(filters)) {
    const first = values?.[0];
    if (FILTER_KEYS.has(key) && first !== undefined && first !== '') {
      out[key] = first;
    }
  }
  return Object.keys(out).length > 0 ? JSON.stringify(out) : null;
}

/** The list is 0-indexed; the table counts from 1. */
export function buildWebshopsQs(input: WebshopsQueryInput): string {
  const qs = new URLSearchParams();
  qs.set('page', String(Math.max(0, input.page - 1)));
  qs.set('count', String(input.limit));
  if (input.sort) qs.set('sort', input.sort);
  const filter = buildFilterJson(input.filters);
  if (filter) qs.set('filter', filter);
  return qs.toString();
}

/** Shared by the route loader and the page hook, keyed on the built query string. */
export function adminWebshopsQueryOptions(input: WebshopsQueryInput) {
  const qs = buildWebshopsQs(input);
  return queryOptions({
    queryKey: ['admin', 'webshops', 'list', qs] as const,
    queryFn: () => fetchAdminWebshops(qs),
    staleTime: 30_000,
  });
}

/** Everything under this prefix — what a write invalidates. */
export const adminWebshopsKey = ['admin', 'webshops'] as const;

export function useAdminWebshops(input: WebshopsQueryInput) {
  const query = useQuery(adminWebshopsQueryOptions(input));

  return {
    ...query,
    rows: query.data?.data ?? [],
    totalCount: query.data?.metadata.pagination.total ?? 0,
  };
}
